/**
 * Extracts the duration of an audio or video file in seconds
 * @param file - The File object (audio or video)
 * @returns Promise that resolves to duration in seconds, or null if unable to extract
 */
export const extractMediaDuration = (file: File): Promise<number | null> => {
  return new Promise((resolve) => {
    // Check if it's an audio or video file
    const isAudio = file.type.startsWith('audio/');
    const isVideo = file.type.startsWith('video/');

    if (!isAudio && !isVideo) {
      resolve(null);
      return;
    }

    // Create appropriate media element
    const mediaElement = isVideo
      ? document.createElement('video')
      : document.createElement('audio');

    // Create object URL for the file
    const objectUrl = URL.createObjectURL(file);

    // Set up event listeners
    const cleanup = () => {
      URL.revokeObjectURL(objectUrl);
      mediaElement.src = '';
    };

    mediaElement.addEventListener('loadedmetadata', () => {
      const duration = mediaElement.duration;
      cleanup();

      // Check if duration is valid (not NaN or Infinity)
      if (isFinite(duration) && duration > 0) {
        resolve(Math.round(duration));
      } else {
        resolve(null);
      }
    });

    mediaElement.addEventListener('error', () => {
      cleanup();
      resolve(null);
    });

    // Set timeout to prevent hanging
    setTimeout(() => {
      cleanup();
      resolve(null);
    }, 10000); // 10 second timeout

    // Start loading metadata
    mediaElement.preload = 'metadata';
    mediaElement.src = objectUrl;
  });
};
